// =====================================================================================
// COPY of the desktop app's src/utils/subscriptionStatus.ts — keep the two identical so the web
// notice bar and the desktop till say the same thing at the same time.
//
// Cloud subscription status for the till — PURE rules (no storage, no network, no clock of their
// own), so they can be checked on their own. The I/O lives in services/subscriptionService.ts
// (desktop) and components/SubscriptionNoticeBar.tsx (web).
//
// Source: GET CompanySubscriptionsPlan/current-plan/{companyId} — the call the web sidebar's
// "Upgrade to PRO (6 days left)" button uses. Billing stops when the subscription or trial ends:
// the server said IsExpired, or the (trusted) clock has passed the end. Never earlier than the real
// end: without the exact end (older server) the latest moment the date / day count allow is used.
// =====================================================================================

/** The top-of-screen notice shows from this many days before the end (paid plans; trials always). */
export const SUBSCRIPTION_WARN_DAYS = 15;

const DAY_MS = 24 * 60 * 60 * 1000;

export const SUBSCRIPTION_EXPIRED_MESSAGE =
  'Your subscription has expired. Please renew your plan to continue processing bills.';
export const TRIAL_EXPIRED_MESSAGE =
  'Your free trial has ended. Please upgrade your plan to continue processing bills.';

/** One answer of the current-plan call, kept so an offline till still knows. */
export interface SubscriptionSnapshot {
  companyId: string;
  packageName: string;
  isTrial: boolean;
  /** The server's verdict when it answered: no subscription runs past "now". */
  isExpired: boolean;
  /** Whole days left when the server answered (its own (EndDate − now).Days). */
  daysRemaining: number;
  /** "yyyy-MM-dd" — the UTC date of the end. */
  endDate: string | null;
  /** The exact end in ms (servers that send EndDateUtc); null before that. */
  endUtcMs: number | null;
  /** When the server answered, on the server's clock when it sent one (ms). */
  fetchedAtMs: number;
  /** Server clock − this PC's clock when it answered (0 when the server sent no time). */
  serverOffsetMs: number;
  /** True when fetchedAtMs / serverOffsetMs come from the server's own clock. */
  serverClock: boolean;
}

export type SubscriptionNoticeTone = 'info' | 'warning' | 'error';

export interface SubscriptionView {
  /** False until the till has heard from the server once (never blocks on its own). */
  known: boolean;
  expired: boolean;
  isTrial: boolean;
  packageName: string;
  /** Whole days left (0 = it ends today); null when unknown or expired. */
  daysLeft: number | null;
  /** "yyyy-MM-dd". */
  endDate: string | null;
  /** Checkout, Complete Sale and Exchange are refused. */
  billingBlocked: boolean;
  /** The message for the top of the screen: a trial, ≤ 15 days left, or expired. */
  notice: { tone: SubscriptionNoticeTone; message: string } | null;
}

export const UNKNOWN_SUBSCRIPTION_VIEW: SubscriptionView = {
  known: false,
  expired: false,
  isTrial: false,
  packageName: '',
  daysLeft: null,
  endDate: null,
  billingBlocked: false,
  notice: null,
};

function pick(source: unknown, ...keys: string[]): unknown {
  if (!source || typeof source !== 'object') return undefined;
  const o = source as Record<string, unknown>;
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null) return o[k];
  }
  return undefined;
}

function finiteNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * An ISO time WITH a zone ("…Z" / "…+05:00") in ms. A zone-less string is refused (null): it is
 * never guessed as UTC or local time.
 */
export function parseZonedIsoMs(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}T/.test(s) || !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(s)) return null;
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Read a current-plan answer (camelCase or PascalCase). `deviceNowMs` is this PC's clock when the
 * answer arrived. Null when the answer carries no plan.
 */
export function snapshotFromCurrentPlan(
  response: unknown,
  companyId: string,
  deviceNowMs: number
): SubscriptionSnapshot | null {
  if (!response || typeof response !== 'object') return null;
  const details = pick(response, 'planDetails', 'PlanDetails');
  const packageName = String(pick(response, 'packageName', 'PackageName') ?? '').trim();
  const daysRemaining = finiteNumber(pick(response, 'daysRemaining', 'DaysRemaining'));
  const endRaw = pick(details, 'endDate', 'EndDate');
  const endDate = typeof endRaw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(endRaw) ? endRaw.slice(0, 10) : null;
  const isExpired = pick(response, 'isExpired', 'IsExpired') === true;
  // Nothing to judge by: not a plan answer.
  if (!packageName && daysRemaining === null && endDate === null) return null;
  if (!isExpired && daysRemaining === null && endDate === null) return null;

  const serverNowMs = parseZonedIsoMs(pick(response, 'serverTimeUtc', 'ServerTimeUtc'));
  return {
    companyId,
    packageName: packageName || 'Subscription',
    isTrial: pick(details, 'isTrial', 'IsTrial') === true,
    isExpired,
    daysRemaining: Math.max(0, Math.floor(daysRemaining ?? 0)),
    endDate,
    endUtcMs: parseZonedIsoMs(pick(response, 'endDateUtc', 'EndDateUtc')),
    fetchedAtMs: serverNowMs ?? deviceNowMs,
    serverOffsetMs: serverNowMs !== null ? serverNowMs - deviceNowMs : 0,
    serverClock: serverNowMs !== null,
  };
}

/**
 * The moment billing stops (ms, server clock). The exact end when the server sent it; otherwise the
 * LATEST moment the end date and the day count allow — a till is never blocked before the real end.
 */
export function subscriptionEndMs(s: SubscriptionSnapshot): number | null {
  if (s.endUtcMs !== null) return s.endUtcMs;
  const bounds: number[] = [];
  if (s.endDate) {
    const [y, m, d] = s.endDate.split('-').map(Number);
    if (y && m && d) bounds.push(Date.UTC(y, m - 1, d + 1)); // the end of that UTC day
  }
  // (EndDate − answeredAt).Days === daysRemaining  ⇒  EndDate < answeredAt + (daysRemaining + 1) days
  if (!s.isExpired) bounds.push(s.fetchedAtMs + (s.daysRemaining + 1) * DAY_MS);
  return bounds.length > 0 ? Math.min(...bounds) : null;
}

function daysWord(n: number): string {
  return n === 1 ? '1 day' : `${n} days`;
}

/** What the till shows and allows at `nowMs` (a trusted clock — see subscriptionService). */
export function evaluateSubscription(s: SubscriptionSnapshot | null, nowMs: number): SubscriptionView {
  if (!s) return UNKNOWN_SUBSCRIPTION_VIEW;
  const end = subscriptionEndMs(s);
  const expired = s.isExpired || (end !== null && nowMs >= end);
  const base = { known: true, isTrial: s.isTrial, packageName: s.packageName, endDate: s.endDate };

  if (expired) {
    return {
      ...base,
      expired: true,
      daysLeft: null,
      billingBlocked: true,
      notice: { tone: 'error', message: s.isTrial ? TRIAL_EXPIRED_MESSAGE : SUBSCRIPTION_EXPIRED_MESSAGE },
    };
  }

  const daysLeft = s.endUtcMs !== null
    ? Math.max(0, Math.floor((s.endUtcMs - nowMs) / DAY_MS))
    : Math.max(0, s.daysRemaining - Math.max(0, Math.floor((nowMs - s.fetchedAtMs) / DAY_MS)));

  let notice: SubscriptionView['notice'] = null;
  if (s.isTrial) {
    const left = daysLeft === 0 ? 'it ends today' : `${daysWord(daysLeft)} left`;
    notice = {
      tone: daysLeft <= 3 ? 'warning' : 'info',
      message: `You are currently using the free trial period (${left}). Upgrade your plan to continue enjoying all features.`,
    };
  } else if (daysLeft <= SUBSCRIPTION_WARN_DAYS) {
    const when = daysLeft === 0 ? 'today' : `in ${daysWord(daysLeft)}`;
    notice = {
      tone: 'warning',
      message: `Your subscription expires ${when}. Please clear your subscription fee to continue using the system without interruption.`,
    };
  }

  return { ...base, expired: false, daysLeft, billingBlocked: false, notice };
}

/** The message shown wherever billing is refused. */
export function billingBlockedMessage(view: Pick<SubscriptionView, 'isTrial'>): string {
  return view.isTrial ? TRIAL_EXPIRED_MESSAGE : SUBSCRIPTION_EXPIRED_MESSAGE;
}
